/**
 * La mano del jugador, anclada abajo.
 *
 * Tres gestos sobre la misma carta, cada uno para algo distinto:
 *
 * - **Arrastrar a una zona.** El gesto natural, el que se siente bien.
 * - **Tocar la carta y luego la zona.** El que funciona siempre: con teclado,
 *   con lector de pantalla y con el pulgar en un autobús.
 * - **Mantener pulsado.** Amplía la carta para leer su efecto. Hace falta porque
 *   en la mano el texto va a 7 píxeles y en el tablero ni siquiera aparece.
 *
 * El destino del arrastre no se calcula con refs sino con
 * `document.elementsFromPoint` sobre el atributo `data-drop-zone`, que además
 * hace que soltar valga en cualquier punto de la columna y no solo sobre un
 * hueco libre.
 */

import { motion } from 'framer-motion';
import { Layers } from 'lucide-react';
import { useRef } from 'react';
import { getCardMaster } from '@/data';
import { useLongPress } from '@/hooks/useLongPress';
import type { CardInstance, ZoneIndex } from '@/types/game';
import { CardView } from './CardView';

interface HandOverlayProps {
  cards: readonly CardInstance[];
  energy: number;
  deckCount: number;
  disabled: boolean;
  selectedCardId: string | null;
  onSelect: (instanceId: string) => void;
  onDropIntoZone: (instanceId: string, zone: ZoneIndex) => void;
  onDragStateChange: (instanceId: string | null) => void;
  /** Mantener pulsada una carta la amplía para poder leer su efecto. */
  onInspect: (instanceId: string) => void;
  /** Carta que el ayudante recomienda jugar, para destacarla. */
  suggestedCardId?: string | null;
}

type DragEndEvent = MouseEvent | TouchEvent | PointerEvent;

/** Coordenadas de viewport del puntero al soltar, con o sin táctil. */
function clientPointOf(event: DragEndEvent): { x: number; y: number } | null {
  if ('changedTouches' in event && event.changedTouches.length > 0) {
    const touch = event.changedTouches[0];
    return touch ? { x: touch.clientX, y: touch.clientY } : null;
  }
  if ('clientX' in event) return { x: event.clientX, y: event.clientY };
  return null;
}

/** Zona bajo el punto donde se soltó, o null si se soltó fuera del tablero. */
function resolveDropZone(event: DragEndEvent): ZoneIndex | null {
  const point = clientPointOf(event);
  if (!point) return null;

  for (const element of document.elementsFromPoint(point.x, point.y)) {
    const holder = element.closest('[data-drop-zone]');
    const raw = holder?.getAttribute('data-drop-zone');
    if (raw === '0') return 0;
    if (raw === '1') return 1;
    if (raw === '2') return 2;
  }
  return null;
}

/**
 * Una carta de la mano.
 *
 * Vive en su propio componente porque necesita el hook de mantener pulsado, y
 * los hooks no pueden llamarse dentro de un `map`.
 */
function HandCard({
  card,
  index,
  overlap,
  affordable,
  selected,
  suggested,
  disabled,
  onSelect,
  onDropIntoZone,
  onDragStateChange,
  onInspect,
}: {
  card: CardInstance;
  index: number;
  overlap: number;
  affordable: boolean;
  selected: boolean;
  suggested: boolean;
  disabled: boolean;
  onSelect: (instanceId: string) => void;
  onDropIntoZone: (instanceId: string, zone: ZoneIndex) => void;
  onDragStateChange: (instanceId: string | null) => void;
  onInspect: (instanceId: string) => void;
}) {
  // Marca si el gesto ya se consumió —por un arrastre o por una pulsación
  // larga—. framer-motion dispara onTap también al final de un arrastre, y sin
  // esto soltar una carta sobre una zona la jugaría y volvería a seleccionarla.
  const consumedRef = useRef(false);

  const longPress = useLongPress(() => {
    consumedRef.current = true;
    onInspect(card.instanceId);
  }, !disabled);

  return (
    <motion.div
      {...longPress}
      // La marca de "gesto consumido" se limpia al EMPEZAR el siguiente, no con
      // un temporizador: una pulsación larga puede durar lo que quiera, y con un
      // plazo fijo soltar el dedo después acababa jugando la carta.
      onPointerDown={(event) => {
        consumedRef.current = false;
        longPress.onPointerDown(event);
      }}
      drag={!disabled}
      dragSnapToOrigin
      dragElastic={0.12}
      dragMomentum={false}
      onDragStart={() => {
        consumedRef.current = true;
        onDragStateChange(card.instanceId);
      }}
      onDragEnd={(event) => {
        onDragStateChange(null);
        const zone = resolveDropZone(event as DragEndEvent);
        if (zone !== null) {
          onDropIntoZone(card.instanceId, zone);
        } else if (!consumedRef.current) {
          // Se soltó fuera de toda zona. Casi siempre es un toque al que el dedo
          // le tembló tres píxeles: framer lo interpreta como arrastre y, si
          // aquí no se hiciera nada, la carta parecería no responder.
          onSelect(card.instanceId);
        }
        consumedRef.current = true;
      }}
      // onTap y no onClick: con `drag` activo, framer-motion captura el puntero
      // y el click nativo puede no dispararse nunca.
      onTap={() => {
        if (consumedRef.current || disabled) return;
        onSelect(card.instanceId);
      }}
      whileDrag={{ scale: 1.2, zIndex: 50, cursor: 'grabbing' }}
      animate={{ y: selected ? -18 : 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={[
        'relative w-16 shrink-0 sm:w-24',
        disabled ? 'cursor-default' : 'cursor-grab',
        // El consejo se marca con un halo y no moviendo la carta: moverla la
        // confundiría con la que está seleccionada.
        suggested ? 'rounded-lg ring-2 ring-emerald-300 ring-offset-2 ring-offset-slate-950' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ touchAction: 'none', marginLeft: index === 0 ? 0 : -overlap }}
    >
      <CardView card={card} variant="hand" playable={affordable && !disabled} selected={selected} />
    </motion.div>
  );
}

export function HandOverlay({
  cards,
  energy,
  deckCount,
  disabled,
  selectedCardId,
  onSelect,
  onDropIntoZone,
  onDragStateChange,
  onInspect,
  suggestedCardId,
}: HandOverlayProps) {
  /**
   * Cuánto se solapan las cartas.
   *
   * Con solape fijo, siete cartas ocupan 328 px y en un móvil de 360 px las de
   * los extremos quedan fuera de la pantalla — y una carta que no se ve no se
   * puede jugar. El solape crece con la mano para que quepan siempre.
   */
  const overlap = cards.length >= 6 ? 30 : cards.length >= 4 ? 22 : 10;

  return (
    <div className="flex items-end gap-2 px-2 pb-2 sm:gap-3 sm:px-4 sm:pb-3">
      <div className="flex shrink-0 flex-col items-center gap-0.5 text-slate-400">
        <Layers className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
        <span className="text-[10px] font-semibold tabular-nums sm:text-xs">{deckCount}</span>
        <span className="sr-only">cartas en el mazo</span>
      </div>

      <div className="flex flex-1 justify-center pt-6">
        {cards.length === 0 ? (
          <p className="pb-4 text-xs text-slate-500">Mano vacía</p>
        ) : (
          cards.map((card, index) => (
            <HandCard
              key={card.instanceId}
              card={card}
              index={index}
              overlap={overlap}
              affordable={getCardMaster(card.masterId).cost <= energy}
              selected={selectedCardId === card.instanceId}
              suggested={suggestedCardId === card.instanceId}
              disabled={disabled}
              onSelect={onSelect}
              onDropIntoZone={onDropIntoZone}
              onDragStateChange={onDragStateChange}
              onInspect={onInspect}
            />
          ))
        )}
      </div>

      {/* Contrapeso del indicador de mazo para que la mano quede centrada. */}
      <div className="w-6 shrink-0 sm:w-8" aria-hidden />
    </div>
  );
}
